import { Component, Input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ButtonModule } from 'primeng/button';
import { Attachment, toAttachments, fileIcon } from '../../utils/file-helper';
import { ActivityService } from 'src/app/services/activity.service';
import { catchError, forkJoin, of } from 'rxjs';
import { FileUploadService } from 'src/app/services/file-upload.service';

@Component({
  selector: 'app-attachments',
  standalone: true,
  imports: [CommonModule, ButtonModule],
  template: `
    <ng-container *ngIf="loading">
      <p>Dateien laden...</p>
    </ng-container>
    <ng-container *ngIf="items.length && !loading">
      <p class="section-title" *ngIf="showTitle">
        <strong>Angehängte Dateien</strong>
      </p>

      <ul class="att-list" role="list">
        <li
          *ngFor="let f of items; trackBy: trackByUrl"
          class="att-row"
          [title]="f.name"
        >
          <div class="att-left">
            <i class="att-icon" [ngClass]="icon(f.ext)" aria-hidden="true"></i>
            <span class="att-badge">Datei</span>
            <span class="att-name">{{ f.name }}</span>
          </div>

          <a
            pButton
            class="p-button-outlined p-button-sm att-open"
            icon="pi pi-external-link"
            label="Öffnen"
            [href]="f.url"
            target="_blank"
            rel="noopener"
            [attr.aria-label]="'Datei öffnen: ' + f.name"
          >
          </a>
        </li>
      </ul>
    </ng-container>
  `,
  styles: [
    `
      .section-title {
        margin: 0.25rem 0 0.5rem;
      }

      .att-list {
        list-style: none;
        margin: 0;
        padding: 0;
        display: flex;
        flex-direction: column;
        gap: 0.5rem;
      }

      .att-row {
        display: grid;
        grid-template-columns: 1fr auto;
        align-items: center;
        gap: 0.75rem;
        padding: 0.5rem 0.75rem;
        border-radius: 0.65rem;
        background: var(--surface-50, #f7f7f8);
      }

      .att-left {
        display: flex;
        align-items: center;
        gap: 0.5rem;
        min-width: 0;
      }
      .att-icon {
        font-size: 1.1rem;
      }
      .att-badge {
        font-size: 0.7rem;
        line-height: 1;
        padding: 0.2rem 0.4rem;
        border-radius: 0.4rem;
        background: var(--surface-100, #eee);
        color: #666;
        text-transform: uppercase;
        letter-spacing: 0.02em;
      }
      .att-name {
        min-width: 0;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        font-weight: 500;
      }

      .att-open {
        font-weight: 600;
        border-radius: 0.6rem;

        outline: none !important;
        box-shadow: none !important;
      }
      .att-open:focus,
      .att-open:focus-visible {
        outline: none !important;
        box-shadow: 0 0 0 0.12rem rgba(0, 0, 0, 0.12) !important;
      }

      .att-open,
      .att-open:link,
      .att-open:visited,
      .att-open:hover,
      .att-open:focus,
      .att-open:active {
        text-decoration: none !important;
      }
    `,
  ],
})
export class AttachmentsComponent {
  // Enthält MinIO-Object-Keys, keine fertigen URLs - der Name folgt den Modell-Feldern
  // (activity.consultation.fileUrls o.ä.), aus denen die Werte gebunden werden.
  @Input() urls: string[] | null | undefined = [];
  @Input() showTitle = true;

  items: Attachment[] = [];
  loading = false;

  constructor(private fileuploadService: FileUploadService) {}

  ngOnChanges() {
    if (!this.urls || this.urls.length === 0) {
      this.items = [];
      return;
    }
    this.resolveUrls();
  }

  // Löst die Object-Keys in presigned URLs auf, die tatsächlich abrufbar sind
  private resolveUrls() {
    this.loading = true;

    const requests = this.urls!.map((objectKey) =>
      this.fileuploadService
        .getPresignedUrl(objectKey)
        .pipe(catchError(() => of(objectKey))),
    );

    forkJoin(requests).subscribe({
      next: (resolvedUrls) => {
        this.items = toAttachments(resolvedUrls);
        this.loading = false;
      },
      error: () => {
        this.items = toAttachments(this.urls!);
        this.loading = false;
      },
    });
  }

  icon(ext: string) {
    return fileIcon(ext);
  }

  trackByUrl = (_: number, f: Attachment) => f.url;
}
